import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useStep } from "flemo";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { answerQuestion, completeExam, getExam } from "@/api/exams";
import {
  useExamQuestionsQuery,
  useIsPlusQuery,
  useNotificationSettingsQuery,
  useTargetScoreQuery,
} from "@/hooks/queries";
import { useInterstitialAd } from "@/hooks/useInterstitialAd";
import { useSpeech } from "@/hooks/useSpeech";
import { trackEvent } from "@/lib/analytics";
import { FREE_REVIEW_NOTE_DAYS, ReportTab, TARGET_SCORE_OPTIONS, TestPhase } from "@/lib/constant";
import { logger } from "@/lib/logger";
import { nativeBridge } from "@/lib/native";
import { isNetworkError, retryOnNetworkError } from "@/lib/networkError";
import {
  cancelIncompleteTestReminder,
  scheduleIncompleteTestReminder,
  scheduleWrongAnswerReview,
  skipTodayStreakReminder,
} from "@/lib/notificationBridge";
import { useAuthStore } from "@/store/authStore";
import { useSettingsStore } from "@/store/settingsStore";
import type { QuizQuestion, QuizResult } from "@/types/common";

import { useCountdown } from "./useCountdown";

const SELECTION_DELAY_MS = 400;
const RECALL_PHASE = "recall" as const;
const MAX_RECALL_COUNT = 3;

type Phase =
  | { type: typeof TestPhase.TEST }
  | { type: typeof RECALL_PHASE }
  | { type: typeof TestPhase.REPORT; tab: ReportTab };

export function selectRecallQuestions(results: QuizResult[]): QuizQuestion[] {
  return results
    .filter(result => !result.isCorrect)
    .slice(0, MAX_RECALL_COUNT)
    .map(result => result.question);
}

export function useQuiz() {
  const { replace } = useNavigate();
  const { id, openScoreModal, entry } = useParams<"/tests/:id">();
  const { popStep, pushStep } = useStep<"/tests/:id">();
  const { speak, isMuted, toggleMute } = useSpeech();
  const { maybeShow: maybeShowAd } = useInterstitialAd();
  const queryClient = useQueryClient();
  const userId = useAuthStore(state => state.user?.id);
  const isScoreModalOpen = openScoreModal ?? false;

  const countdownSeconds = useSettingsStore(state => state.countdownSeconds);
  const { data: targetScore = TARGET_SCORE_OPTIONS[2] } = useTargetScoreQuery();
  const { data: notificationSettings } = useNotificationSettingsQuery();
  const { data: isPlus = false } = useIsPlusQuery();
  const { data: exam } = useQuery({
    queryKey: ["exam", id],
    queryFn: () => getExam(id!),
    enabled: Boolean(id),
    staleTime: 1000 * 60 * 10,
  });

  const isAdvancingRef = useRef(false);
  const selectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [phase, setPhase] = useState<Phase>({ type: TestPhase.TEST });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [pendingSelectedOptionIndex, setPendingSelectedOptionIndex] = useState<number | null>(null);
  const [results, setResults] = useState<QuizResult[]>([]);
  const [recallQuestions, setRecallQuestions] = useState<QuizQuestion[]>([]);
  const [recallCurrentIndex, setRecallCurrentIndex] = useState(0);
  const [recallCorrectCount, setRecallCorrectCount] = useState(0);
  const [pendingRecallSelectedOptionIndex, setPendingRecallSelectedOptionIndex] = useState<number | null>(null);
  const [noteOnlyCorrect, setNoteOnlyCorrect] = useState(false);
  const recallPromptTrackedRef = useRef(false);

  /** 서버(`answered_at` 기준) 답변 수와 시험 id — 캐시 갱신 시에만 로컬 인덱스를 다시 맞춤 */
  const serverSyncRef = useRef<{ examId: string | undefined; answeredCount: number }>({
    examId: undefined,
    answeredCount: -1,
  });

  // 시험 문항 조회
  const { data, isPending, isError } = useExamQuestionsQuery(id);
  const questions = data?.questions ?? [];
  const answeredResults = data?.answeredResults ?? [];

  const currentQuestion = questions[currentIndex] ?? null;
  const recallCurrentQuestion = recallQuestions[recallCurrentIndex] ?? null;
  const wrongCount = results.filter(result => !result.isCorrect).length;
  const recallCount = recallQuestions.length;
  const score = results.length > 0 ? Math.round((results.filter(r => r.isCorrect).length / results.length) * 100) : 0;

  // 무료 유저 + 완료 후 3일 경과 → Review Note 잠금
  // completed_at이 없으면(진행 중) 잠금 없음
  const isReviewNoteLocked = useMemo(() => {
    if (isPlus || !exam?.completed_at) {
      return false;
    }

    const completedAt = new Date(exam.completed_at);
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - FREE_REVIEW_NOTE_DAYS);

    return completedAt < cutoff;
  }, [isPlus, exam?.completed_at]);

  // 선택 타임아웃 정리
  useEffect(() => {
    return () => {
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
    };
  }, []);

  // 서버에 저장된 답변 수에 맞춰 복원·갱신 (캐시 stale로 이탈 후 재진입 시 인덱스 어긋남 방지)
  useEffect(() => {
    if (!id || questions.length === 0) {
      return;
    }

    const n = answeredResults.length;
    const prev = serverSyncRef.current;

    if (prev.examId === id && n === prev.answeredCount) {
      return;
    }

    if (n >= questions.length) {
      setResults(answeredResults);
      // 마지막 응답 캐시 갱신이 재회상 시작 직후 도착해도 루프를 리포트로 덮어쓰지 않는다.
      setPhase(currentPhase =>
        currentPhase.type === RECALL_PHASE ? currentPhase : { type: TestPhase.REPORT, tab: ReportTab.CARD },
      );
    } else {
      if (n === 0) {
        trackEvent("quiz_started", {
          exam_id: id,
          ...(entry === "onboarding_starter" ? { entry, is_starter: true } : {}),
          total_questions: questions.length,
          target_score: targetScore,
          countdown_seconds: countdownSeconds,
        });
      }

      // 아직 진행 중인 시험 → 설정이 켜져 있을 때만 미완료 테스트 알림 예약
      if (notificationSettings?.enabled && notificationSettings?.incomplete_test) {
        scheduleIncompleteTestReminder();
      }

      setResults(answeredResults);
      setCurrentIndex(n);
    }

    serverSyncRef.current = { examId: id, answeredCount: n };
  }, [id, entry, questions.length, answeredResults]);

  // 점수 모달에 재회상 선택지가 실제로 노출된 시점은 한 번만 기록한다.
  useEffect(() => {
    if (!isScoreModalOpen || recallCount === 0 || recallPromptTrackedRef.current) {
      return;
    }

    recallPromptTrackedRef.current = true;
    trackEvent("quiz_recall_prompt_shown", {
      exam_id: id,
      ...(entry === "onboarding_starter" ? { entry, is_starter: true } : {}),
      wrong_count: wrongCount,
      recall_count: recallCount,
      correct_count: 0,
    });
  }, [id, entry, isScoreModalOpen, recallCount, wrongCount]);

  // 단어 TTS
  useEffect(() => {
    if (isMuted) {
      return;
    }

    const questionToSpeak = phase.type === RECALL_PHASE ? recallCurrentQuestion : currentQuestion;

    if ((phase.type === TestPhase.TEST || phase.type === RECALL_PHASE) && questionToSpeak) {
      speak(questionToSpeak.word);
    }
  }, [phase.type, isMuted, currentQuestion?.word, recallCurrentQuestion?.word, speak]);

  // 다음 문제로 이동
  async function advanceToNext(selectedOptionIndex: number | null) {
    const question = questions[currentIndex];

    if (!question) {
      return;
    }

    const isCorrect = selectedOptionIndex === question.correctIndex;

    trackEvent("question_answered", {
      exam_id: id,
      ...(entry === "onboarding_starter" ? { entry, is_starter: true } : {}),
      question_index: currentIndex,
      is_correct: isCorrect,
      is_skipped: selectedOptionIndex === null,
    });

    // DB에 응답 저장 (일시적 네트워크 오류는 1회 재시도)
    try {
      await retryOnNetworkError(() =>
        answerQuestion(question.id, selectedOptionIndex !== null ? selectedOptionIndex + 1 : null, isCorrect),
      );
      void queryClient.invalidateQueries({ queryKey: ["examQuestions", id] });
    } catch (error) {
      // 네트워크 실패(오프라인 등)는 코드 결함이 아니므로 Sentry 에러 대신 breadcrumb으로 남긴다
      const logLevel = isNetworkError(error) ? "warn" : "error";

      logger[logLevel]({
        domain: "quiz",
        message: "답변 저장 실패",
        error,
        data: { examId: id, questionIndex: currentIndex },
      });
      toast.error("답변 저장에 실패했어요. 네트워크 연결을 확인하고 다시 선택해 주세요.", {
        richColors: true,
        position: "top-center",
      });

      return;
    }

    setResults(prev => [...prev, { question, selectedOptionIndex, isCorrect }]);

    if (currentIndex >= questions.length - 1) {
      // 시험 완료 처리 — 미완료 테스트 취소 + 오늘 스트릭 건너뛰기 (내일 재예약)
      cancelIncompleteTestReminder();
      skipTodayStreakReminder();

      // 오답 복습 알림: 오답이 있고 설정이 켜져 있을 때만 1회성 예약
      const allResults = [...results, { question, selectedOptionIndex, isCorrect }];
      const recallQueue = selectRecallQuestions(allResults);
      const hasWrongAnswers = recallQueue.length > 0;

      if (hasWrongAnswers && notificationSettings?.enabled && notificationSettings?.wrong_answer_review) {
        scheduleWrongAnswerReview(notificationSettings.wrong_answer_timing);
      }

      try {
        await retryOnNetworkError(() => completeExam(id));
      } catch (error) {
        const logLevel = isNetworkError(error) ? "warn" : "error";

        logger[logLevel]({ domain: "quiz", message: "시험 완료 처리 실패", error, data: { examId: id } });
      } finally {
        void queryClient.invalidateQueries({ queryKey: ["examQuestions", id] });
        void queryClient.invalidateQueries({ queryKey: ["testDates", userId] });
      }

      const correctCount = allResults.filter(r => r.isCorrect).length;

      trackEvent("quiz_completed", {
        exam_id: id,
        ...(entry === "onboarding_starter" ? { entry, is_starter: true } : {}),
        total_questions: allResults.length,
        correct_count: correctCount,
        score: Math.round((correctCount / allResults.length) * 100),
      });
      trackEvent("daily_study_completed", {
        source: "test",
        exam_id: id,
        ...(entry === "onboarding_starter" ? { entry, is_starter: true } : {}),
        total_questions: allResults.length,
        correct_count: correctCount,
        score: Math.round((correctCount / allResults.length) * 100),
      });

      void maybeShowAd("quiz_complete");
      setRecallQuestions(recallQueue);
      setRecallCurrentIndex(0);
      setRecallCorrectCount(0);
      recallPromptTrackedRef.current = false;
      pushStep({ id, openScoreModal: true });
      setPhase({ type: TestPhase.REPORT, tab: ReportTab.CARD });
      return;
    }

    setCurrentIndex(prev => prev + 1);
  }

  // 다음 문제로 이동 시작
  async function startAdvance(selectedOptionIndex: number | null) {
    if (isAdvancingRef.current) {
      return;
    }

    isAdvancingRef.current = true;
    try {
      await advanceToNext(selectedOptionIndex);
    } finally {
      setPendingSelectedOptionIndex(null);
      isAdvancingRef.current = false;
    }
  }

  // 카운트다운 (퀴즈 단계에서만, 문제 변경 시 리셋)
  const countdown = useCountdown(
    phase.type === TestPhase.TEST && questions.length > 0,
    countdownSeconds,
    currentIndex,
    () => void startAdvance(null),
  );

  function startRecall() {
    if (recallCount === 0) {
      popStep();
      return;
    }

    trackEvent("quiz_recall_started", {
      exam_id: id,
      ...(entry === "onboarding_starter" ? { entry, is_starter: true } : {}),
      wrong_count: wrongCount,
      recall_count: recallCount,
      correct_count: 0,
    });
    setRecallCurrentIndex(0);
    setRecallCorrectCount(0);
    setPendingRecallSelectedOptionIndex(null);
    setPhase({ type: RECALL_PHASE });
    popStep();
  }

  function skipRecall() {
    trackEvent("quiz_recall_skipped", {
      exam_id: id,
      ...(entry === "onboarding_starter" ? { entry, is_starter: true } : {}),
      wrong_count: wrongCount,
      recall_count: recallCount,
      correct_count: recallCorrectCount,
    });
    setRecallQuestions([]);
    setPendingRecallSelectedOptionIndex(null);
    setPhase({ type: TestPhase.REPORT, tab: ReportTab.CARD });

    if (phase.type !== RECALL_PHASE) {
      popStep();
    }
  }

  function selectRecallOption(optionIndex: number) {
    if (!recallCurrentQuestion || pendingRecallSelectedOptionIndex !== null) {
      return;
    }

    nativeBridge.system.haptic("light");
    setPendingRecallSelectedOptionIndex(optionIndex);

    const isCorrect = optionIndex === recallCurrentQuestion.correctIndex;
    const nextCorrectCount = recallCorrectCount + (isCorrect ? 1 : 0);

    trackEvent("quiz_recall_question_answered", {
      exam_id: id,
      ...(entry === "onboarding_starter" ? { entry, is_starter: true } : {}),
      wrong_count: wrongCount,
      recall_count: recallCount,
      correct_count: nextCorrectCount,
      question_index: recallCurrentIndex,
      is_correct: isCorrect,
    });

    selectionTimeoutRef.current = setTimeout(() => {
      setRecallCorrectCount(nextCorrectCount);
      setPendingRecallSelectedOptionIndex(null);
      selectionTimeoutRef.current = null;

      if (recallCurrentIndex >= recallCount - 1) {
        trackEvent("quiz_recall_completed", {
          exam_id: id,
          ...(entry === "onboarding_starter" ? { entry, is_starter: true } : {}),
          wrong_count: wrongCount,
          recall_count: recallCount,
          correct_count: nextCorrectCount,
        });
        setRecallQuestions([]);
        setPhase({ type: TestPhase.REPORT, tab: ReportTab.CARD });
        return;
      }

      setRecallCurrentIndex(index => index + 1);
    }, SELECTION_DELAY_MS);
  }

  return {
    phase,
    questions,
    isLoading: Boolean(id) && isPending,
    isError,
    currentIndex,
    currentQuestion,
    recallCurrentQuestion,
    recallCurrentIndex,
    recallCount,
    recallCorrectCount,
    countdown,
    results,
    score,
    isPassed: score >= targetScore,
    isMuted,
    pendingSelectedOptionIndex,
    pendingRecallSelectedOptionIndex,
    isScoreModalOpen,
    noteOnlyCorrect,
    isReviewNoteLocked,
    selectOption: (optionIndex: number) => {
      if (isAdvancingRef.current) {
        return;
      }

      nativeBridge.system.haptic("light");
      isAdvancingRef.current = true;
      setPendingSelectedOptionIndex(optionIndex);
      selectionTimeoutRef.current = setTimeout(async () => {
        try {
          await advanceToNext(optionIndex);
        } finally {
          setPendingSelectedOptionIndex(null);
          isAdvancingRef.current = false;
          selectionTimeoutRef.current = null;
        }
      }, SELECTION_DELAY_MS);
    },
    skipToNext: () => void startAdvance(null),
    closeScoreModal: popStep,
    startRecall,
    skipRecall,
    selectRecallOption,
    setReportTab: (tab: ReportTab) => {
      trackEvent("quiz_report_tab_changed", { exam_id: id, tab });

      if (tab === ReportTab.CARD && noteOnlyCorrect) {
        setNoteOnlyCorrect(false);
      }

      setPhase({ type: TestPhase.REPORT, tab });
    },
    toggleNoteFilter: () => setNoteOnlyCorrect(v => !v),
    exitToList: () => {
      if (phase.type !== TestPhase.REPORT) {
        void trackEvent("quiz_exited", {
          exam_id: id,
          ...(entry === "onboarding_starter" ? { entry, is_starter: true } : {}),
          phase: phase.type,
          question_index: phase.type === RECALL_PHASE ? recallCurrentIndex : currentIndex,
          answered_count: results.length,
          total_questions: questions.length,
        });
      }
      replace("/tests", {}, { transitionName: "none" });
    },
    speakWord: (text: string) => speak(text),
    toggleMute: () => {
      if (isMuted && phase.type === TestPhase.TEST && currentQuestion) {
        speak(currentQuestion.word);
      }
      toggleMute();
    },
  };
}
